<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/intake'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    :ref="'sample-code'"
    :defaultUserSettings="false"
  >
    <template #panelNotice>
      <div
        class="split-view-panel-notice split-view-panel-notice--amber"
        v-if="riskNoticeText"
      >
        <div class="split-view-panel-notice-title">
          <svg
            class="split-view-panel-notice-icon"
            viewBox="0 0 24 24"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
          </svg>
          Historical grading/payment risk
        </div>
        {{ riskNoticeText }}
      </div>
    </template>
  </SplitViewTemplate>
</template>
<script>
import { FeatureStorage } from "@/_services/storage";
import ApiService from "@/_services/api/api.service";
import { SplitViewService } from "../../../_services/api/templates/split-view.services";
import SplitViewTemplate from "../../../templates/split-view";
import RouteMixin from "@/_mixins/route.mixin";

// Real codes are F/G/M only (technical-design.md section 8, DF-12) - the
// original Concept's invented "General/Fruit/Moisture" labels were wrong.
const TYPE_OPTIONS = [
  { value: "F", label: "Foreign Material" },
  { value: "G", label: "Grade" },
  { value: "M", label: "Moisture" },
];

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      validationSummary: [],
      codeMatched: false,
      lastCheckedCode: undefined,
      // BR-12/AD-05: risk-relevant fields (Grade Cut-Off % A-F/Is Grade?/GL
      // Account) as originally loaded, captured in recordPostProcess -
      // compared against the save payload to decide whether an Edit needs
      // the blocking confirmation. Undefined on Create (always risk-relevant
      // regardless of any snapshot).
      originalRiskSnapshot: undefined,
      formModel: {
        RecordId: undefined,
        Code: null,
        Description: null,
        Type: null,
        // Not yet exposed as form inputs (Stories 2-5 ship their own sections)
        // but declared here so an Identification-only save round-trips every
        // other field unchanged instead of resetting it to a wire default.
        ForeignMaterial: null,
        Fruit: null,
        Rank: null,
        IsGrade: null,
        GroupWith: null,
        IsVisibleOnGradingDetails: null,
        GradeCutOffA: null,
        GradeCutOffB: null,
        GradeCutOffC: null,
        GradeCutOffD: null,
        GradeCutOffE: null,
        GradeCutOffF: null,
        GLAccount: null,
        Inactive: true,
        // DF-09/AD-05: stays false until Story 6's blocking confirmation modal
        // sets it true - every Create is rejected server-side until then,
        // which is the intended fail-closed behaviour, not a defect.
        Confirmed: false,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
      },
      data: {
        title: "Sample Code",
        pageRoute: "/sample-code",
        saveEndPoint: "Save_AlmondcoSampleCode",
        recordEndPoint: "AlmondcoSampleCode",
        exportFileName: "Sample Code List",
        recordPostProcess: this.recordPostProcess,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoSampleCode",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "Code", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "sample-code",
              mode: "review",
              idProperty: "RecordId",
              idMode: "Guid",
              rowSelectCallBack: () => {
                this.beforeInit();
              },
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/sample-code/create",
                clickAction: () => {
                  this.goToCreateSampleCode();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoSampleCode",
                  "create",
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              dataField: "Description",
              label: "Sample Code",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "Description",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
            },
            {
              screenField: "Code",
              dataField: "Code",
              label: "Code",
              visible: false,
              showInColumnChooser: true,
            },
            {
              screenField: "Description",
              dataField: "Description",
              label: "Description",
              visible: false,
              showInColumnChooser: true,
            },
            {
              screenField: "Type",
              dataField: "Type",
              label: "Type",
              calculateCellValue: (rowData) => this.typeLabel(rowData.Type),
            },
            {
              screenField: "AlmondcoSampleCodeType",
              dataField: "Type",
              filterDataField: "Type",
              filterIndex: 1,
              visible: false,
              showInColumnChooser: false,
            },
            {
              screenField: "TypeDescription",
              dataField: "TypeDescription",
              label: "Type Description",
              visible: false,
              showInColumnChooser: false,
            },
            {
              screenField: "Rank",
              dataField: "Rank",
              label: "Rank",
              dataType: "number",
              alignment: "left",
            },
            {
              screenField: "Status",
              width: "auto",
              filterIndex: 0,
            },
          ],
          valueChangedEventListners: [],
        },
      },
    };
  },
  computed: {
    setFormConfig() {
      const form = {
        headerToolbar: {
          closeSplit: true,
          save: this.setSaveOption,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
              ],
            },
          ],
          toolbarFormItems: [
            {
              screenField: "Inactive",
              editorType: "dxSwitch",
              editorOptions: {
                readOnly: false,
              },
              cssClass: "toolbar-customer-label",
            },
          ],
          toolbarFormLocation: "after",
          editCallBack: () => {
            this.beforeInit();
          },
        },
        formItems: [
          {
            itemType: "group",
            caption: "Identification",
            colCount: 2,
            items: [
              {
                dataField: "Code",
                editorType: "dxTextBox",
                editorOptions: {
                  maxLength: 6,
                  tabIndex: 1,
                  onFocusOut: () => {
                    this.onCodeBlur();
                  },
                },
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Code" },
                visible: true,
                allowUpdate: this.baseView.mode == "create",
              },
              {
                dataField: "Description",
                editorType: "dxTextBox",
                editorOptions: {
                  maxLength: 30,
                  tabIndex: 2,
                },
                // 10Sep2026 (SD), Bug #77820: required removed - DF-04's own
                // revision (making Description required, a deliberate
                // deviation from Concept's original "leave optional"
                // recommendation) is reversed per PO direction: real Sample
                // data has precedent for a blank Description, matching
                // legacy's own behaviour (dbo.ALMONDCO_SAMPLE_CODE.
                // SAMPLE_DESCN is NOT NULL DEFAULT (''), not a "must be
                // non-blank" constraint). maxLength: 30 above is kept -
                // only requiredness changes. Paired with the server-side
                // removal of [Required] on AlmondcoSampleCode.Description
                // (GcorpAPI/Production/Models/AlmondcoSampleCode.cs) -
                // removing this client-side check alone is not sufficient,
                // see this project's investigation.md (Almondco - 2116 -
                // Sample Code Master) for the full root-cause writeup.
                label: { text: "Description" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "Classification",
            colCount: 2,
            items: [
              {
                dataField: "Type",
                editorType: "dxSelectBox",
                editorOptions: {
                  items: TYPE_OPTIONS,
                  valueExpr: "value",
                  displayExpr: "label",
                  searchEnabled: true,
                  showClearButton: true,
                  tabIndex: 3,
                },
                required: true,
                label: { text: "Type" },
                visible: true,
              },
              {
                itemType: "empty",
              },
              {
                dataField: "ForeignMaterial",
                editorType: "dxSwitch",
                editorOptions: {
                  tabIndex: 4,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option(
                      "value",
                      this.formModel.ForeignMaterial == "Y",
                    );
                    e.component.endUpdate();
                  },
                },
                label: { text: "Foreign Material" },
                visible: true,
              },
              {
                dataField: "Fruit",
                editorType: "dxSwitch",
                editorOptions: {
                  tabIndex: 5,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option("value", this.formModel.Fruit == "Y");
                    e.component.endUpdate();
                  },
                },
                label: { text: "Fruit" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "Grading Configuration",
            colCount: 2,
            items: [
              {
                dataField: "Rank",
                editorType: "dxNumberBox",
                editorOptions: {
                  // Bug #77365 fix, corrected: format's own digit-count
                  // caps are governed entirely by devextreme's LDML number
                  // parser (esm/localization/ldml/number.js getFormatter) -
                  // maxIntegerPrecision is set to the "0" count ONLY when
                  // the integer part has zero "#" characters; if even one
                  // "#" is present, the max is unbounded regardless of how
                  // many placeholders exist. "0" (no "#") is why entry
                  // capped at exactly 1 digit; the first attempt at this fix
                  // ("0##") reintroduced a "#", which lifted the cap
                  // entirely instead of widening it to 3 - confirmed
                  // empirically (could type 7+ digits) and by reading that
                  // parser directly. "#0" (one "#", one "0" - the same
                  // integer-part shape this file's own GradeCutOffA/B/etc.
                  // fields already use via "#0.00") keeps typing unbounded
                  // by the mask, same as those fields, and relies on max
                  // below for the actual ceiling instead - the only
                  // reliable way to get "typeable up to at least 3 digits,
                  // no forced zero-padding, real upper bound" out of this
                  // widget. max: 999 added per the bug's own Expected
                  // Outcome, now also confirmed wanted (a value like
                  // 5656666 getting through was reported as the regression
                  // this correction fixes). See investigation.md under this
                  // project folder (Bug #77365) for the full trace.
                  format: "#0",
                  min: 0,
                  max: 999,
                  tabIndex: 6,
                },
                label: { text: "Rank" },
                visible: true,
              },
              {
                // BR-07: fully independent of Type - never derived (real
                // exception W/S). No registered ScreenField, same as
                // ForeignMaterial/Fruit above.
                dataField: "IsGrade",
                editorType: "dxSwitch",
                editorOptions: {
                  tabIndex: 7,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option("value", this.formModel.IsGrade == "Y");
                    e.component.endUpdate();
                  },
                },
                label: { text: "Is Grade?" },
                visible: true,
              },
              {
                // BR-08: active Sample Codes only, excluding this record's own
                // Code (prototype's populateGroupWith(excludeCode)). Blank
                // defaults to the record's own Code - already handled server-
                // side in AlmondcoSampleCodeBusiness.ValidateGroupWith (#76061),
                // not duplicated here. A live OData dataSource (not the grid's
                // client-side snapshot via getGridItems()) - pulling from the ref
                // only worked for onCodeBlur/overWritePayload because those run
                // well after mount; here setFormConfig evaluates at render time,
                // often before the grid has loaded, and a plain array captured
                // then never refreshes since Vue doesn't track that ref read as
                // reactive. A remote ODataStore also resolves the bound value
                // correctly via byKey() even if it isn't on the loaded page.
                //
                // The `baseView.mode !== "create"` guard is deliberate, not
                // just belt-and-braces: setFormConfig is a computed property,
                // so reading this.formModel.Code here makes Vue track it as a
                // dependency and rebuild this WHOLE form config (a fresh Code
                // textbox config, fresh onFocusOut closure, fresh dataSource
                // instance) on every commit to Code. On Create, that happens
                // right as the user tabs off Code - Vue's own reactive write
                // rebuilds the Code widget out from under its own blur event,
                // which was the actual cause of "works on the second blur,
                // not the first" (reading e.component.option("value") wasn't
                // enough, since the widget itself was being torn down/rebuilt
                // mid-event). The `&&` short-circuit means formModel.Code is
                // never touched during Create at all, so typing it no longer
                // retriggers this computed - and it's a no-op to exclude
                // anyway, since a Code that doesn't exist yet can't already
                // be in the active-Sample-Codes list. In Edit/Review, Code is
                // locked (BR-02) so this dependency is safe - it only
                // resolves once, on load.
                dataField: "GroupWith",
                editorType: "dxSelectBox",
                editorOptions: {
                  dataSource: ApiService.createDataSource(
                    "api/AlmondcoSampleCode",
                    {
                      select: ["Code", "Description"],
                      filter:
                        this.baseView.mode !== "create" && this.formModel.Code
                          ? [
                              ["Inactive", "=", "N"],
                              "and",
                              ["Code", "<>", this.formModel.Code],
                            ]
                          : ["Inactive", "=", "N"],
                    },
                  ),
                  valueExpr: "Code",
                  displayExpr: (item) =>
                    item
                      ? `${item.Description || item.Code} - ${item.Code}`
                      : "",
                  searchEnabled: true,
                  showClearButton: true,
                  placeholder: "— None (defaults to own Code on save) —",
                  tabIndex: 8,
                },
                label: { text: "Group With" },
                visible: true,
              },
              {
                dataField: "IsVisibleOnGradingDetails",
                editorType: "dxSwitch",
                editorOptions: {
                  tabIndex: 9,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option(
                      "value",
                      this.formModel.IsVisibleOnGradingDetails == "Y",
                    );
                    e.component.endUpdate();
                  },
                },
                label: { text: "Visible On Grading Details?" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "Grade Cut-Off % (A-F)",
            colCount: 6,
            items: [
              {
                dataField: "GradeCutOffA",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 10,
                },
                label: { text: "A", location: "top", alignment: "center" },
                visible: true,
              },
              {
                dataField: "GradeCutOffB",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 11,
                },
                label: { text: "B", location: "top", alignment: "center" },
                visible: true,
              },
              {
                dataField: "GradeCutOffC",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 12,
                },
                label: { text: "C", location: "top", alignment: "center" },
                visible: true,
              },
              {
                dataField: "GradeCutOffD",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 13,
                },
                label: { text: "D", location: "top", alignment: "center" },
                visible: true,
              },
              {
                dataField: "GradeCutOffE",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 14,
                },
                label: { text: "E", location: "top", alignment: "center" },
                visible: true,
              },
              {
                dataField: "GradeCutOffF",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#0.00",
                  min: 0,
                  max: 100,
                  tabIndex: 15,
                },
                label: { text: "F", location: "top", alignment: "center" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "GL Account",
            colCount: 1,
            items: [
              {
                screenField: "GLChart",
                dataField: "GLAccount",
                editorType: "dxSelectBox",
                valueExpr: "Code",
                displayExpr: (item) =>
                  item
                    ? `${item.Description} - ${item.ComputedStringCode}`
                    : "",
                editorOptions: {
                  searchEnabled: true,
                  showClearButton: true,
                  placeholder: "— None —",
                  tabIndex: 16,
                  onInitialized: (e) => {
                    if (!this.formModel.GLAccount) {
                      e.component.beginUpdate();
                      e.component.option("value", null);
                      e.component.endUpdate();
                    }
                  },
                },
                label: { text: "GL Account" },
                visible: true,
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
      };

      return form;
    },
    setSaveOption() {
      return FeatureStorage.checkPermission(
        this.$route.meta.applicationFeatureCode,
        this.baseView.mode == "create" ? "create" : "edit",
      );
    },
    /**
     * BR-12/DF-09: persistent risk-notice body text, specific to Create vs
     * an existing record (Edit/Review) - undefined (hides the notice
     * entirely, see the panelNotice slot's own v-if) on the plain list.
     */
    riskNoticeText() {
      if (this.baseView.mode === "create") {
        return "A newly-created Sample Code can be picked up by a grading/payment recalculation covering a period before it existed, changing that period's result — this table has no season/date scoping to prevent it.";
      }
      if (this.baseView.mode === "edit" || this.baseView.mode === "review") {
        return "A grading/payment calculation already performed using this Sample Code's Grade Cut-Off %, Is Grade?, or GL Account cannot be recreated if those values change — this table has no season/date scoping. Other fields do not carry this risk.";
      }
      return undefined;
    },
  },
  methods: {
    /**
     * BR-12/AD-05: $confirm(message, title)'s default width is unbounded -
     * fine for the short messages every other screen's $confirm call uses,
     * but this dialog's long risk-explanation text renders it almost full-
     * screen-wide (confirmed on a wide monitor). Passing the object form
     * instead of the plain (message, title) string form is the only way to
     * set a width - object form requires the Yes/No buttons to be supplied
     * explicitly too, since it bypasses $confirm's own default-buttons
     * wrapping. Scoped to this screen only; the shared $confirm plugin and
     * every other caller (Console Season Year, route.mixin's leave-guard)
     * are untouched.
     */
    confirmRiskChange(message, title) {
      return this.$confirm({
        title,
        messageHtml: message,
        showTitle: true,
        width: 500,
        buttons: [
          { text: "Yes", onClick: () => true },
          { text: "No", onClick: () => false },
        ],
      });
    },
    typeLabel(type) {
      const match = TYPE_OPTIONS.find((o) => o.value === type);
      return match ? match.label : "—";
    },
    goToCreateSampleCode() {
      this.$router
        .push({
          path: `/sample-code/create`,
        })
        .then(() => {
          this.beforeInit();
        });
    },
    beforeInit() {
      this.codeMatched = false;
      this.lastCheckedCode = undefined;
    },
    /**
     * BR-01/DF-15 backing lookup, case-insensitive - a live OData query
     * rather than the grid's own client-side snapshot. The grid's data
     * hasn't necessarily finished its first load yet by the time the panel
     * opens and the user tabs off Code the very first time (confirmed: the
     * blur handler would silently find nothing on that first attempt, then
     * work on a second attempt once the grid had caught up) - the same
     * staleness class of bug already fixed for Group With/GL Account earlier
     * in this feature by switching those lookups to a live dataSource too.
     */
    async findExistingSampleCode(code) {
      if (!code) return null;
      const escapedCode = code.toLowerCase().replace(/'/g, "''");
      // Bug #77366 fix: escapedCode above only doubles OData's own
      // single-quote string-literal escape - it never made the value safe
      // to sit inside a URL query string. A raw Code containing a bare "%"
      // (e.g. "@%\@") reached the shared EncodeODataQuery helper
      // (_helpers/network/requests.js) unencoded, which assumes every query
      // value it sees is already percent-encoded and unconditionally calls
      // decodeURIComponent on it - a "%" not followed by two hex digits
      // throws URIError: URI malformed there. encodeURIComponent must run
      // AFTER the single-quote doubling above, not instead of it - it
      // leaves a literal ' unescaped by design, so this ordering keeps
      // OData's own literal-quote escaping intact while also making the
      // whole segment URL-safe. See investigation.md under this project
      // folder (Bug #77366) for the full root-cause trace, including why
      // this crash was reached twice - once from this method's own blur-
      // time caller (onCodeBlur), and again from overWritePayload's Save-
      // time duplicate re-check - which is what turned a console error on
      // blur into "Save silently does nothing."
      const response = await SplitViewService.getByCode(
        "AlmondcoSampleCode",
        `tolower(Code) eq '${encodeURIComponent(escapedCode)}'`,
      );
      return response && response.data && response.data.value.length > 0
        ? response.data.value[0]
        : null;
    },
    /**
     * BR-01/DF-15: a Code matching an existing record - case-insensitively,
     * never uppercased - loads that record for review instead of creating a
     * duplicate (legacy's own load-and-switch UX, AD-03). Unlike Moisture
     * Threshold's upsert-by-Season shape, Sample Code's write is keyed on
     * RecordId, so a match must switch the panel into Edit for that
     * RecordId, not just prefill fields while staying in Create.
     */
    async onCodeBlur() {
      if (this.baseView.mode !== "create") return;

      const code = this.formModel.Code;
      if (!code) return;
      if (this.lastCheckedCode === code) return;
      this.lastCheckedCode = code;

      const match = await this.findExistingSampleCode(code);
      if (match) {
        this.codeMatched = true;
        this.$notify(
          `Code ${match.Code} already exists - loaded the existing record instead of creating a duplicate.`,
          "info",
        );
        // Deliberate load-and-switch (BR-01) - suppress route.mixin's generic
        // "unsaved changes" prompt, which would otherwise fire here since the
        // Create form is dirty the moment a Code has been typed.
        this.setIgnoreRouteChangeConfirmation();
        this.$router.push({
          path: `/sample-code/edit/${match.RecordId}`,
        });
      } else {
        this.codeMatched = false;
      }
    },
    /**
     * Save-time re-check (mirrors Moisture Threshold's own), regardless of
     * blur outcome - covers Enter-key submission or any path that skips
     * blur. Insert()'s server-side duplicate rejection (BR-01 backstop) is
     * still the authoritative guard; this only keeps the create/edit UX
     * honest when a match exists.
     *
     * BR-12/AD-05: also the DF-09 risk-confirm gate. Async (split-view.vue's
     * valueChangeCallBack now awaits this) so it can block Save on
     * this.$confirm() (Popup Variant A, same dialog Console Season Year
     * already uses) and cancel the save entirely by returning a falsy value
     * if the user declines - matching the prototype's Create-always /
     * Edit-only-if-changed behaviour exactly, including its wording.
     */
    async overWritePayload(payload) {
      if (
        this.baseView.mode === "create" &&
        !this.codeMatched &&
        payload.Code
      ) {
        const match = await this.findExistingSampleCode(payload.Code);
        if (match) {
          this.$notify(
            `Code ${payload.Code} already exists - it will be updated instead of created.`,
            "info",
          );
          payload.RecordId = match.RecordId;
        }
      }

      // A field the user never touched this session stays whatever raw "Y"/"N"
      // string it was loaded with (the dxSwitch's onInitialized sets its
      // displayed value inside beginUpdate()/endUpdate(), which suppresses
      // onValueChanged and so never writes a boolean back into formModel) -
      // only a genuine user toggle converts it to a boolean via the widget's
      // own onValueChanged. Checking `=== true` alone treated every untouched
      // "Y" as falsy and silently flipped it to "N" on every single save.
      const toYN = (value) => (value === true || value === "Y" ? "Y" : "N");
      payload.ForeignMaterial = toYN(payload.ForeignMaterial);
      payload.Fruit = toYN(payload.Fruit);
      payload.IsGrade = toYN(payload.IsGrade);
      payload.IsVisibleOnGradingDetails = toYN(
        payload.IsVisibleOnGradingDetails,
      );
      payload.GLAccount = payload.GLAccount ? payload.GLAccount : 0;

      const isCreate = this.baseView.mode === "create";
      const riskFieldLabels = {
        GradeCutOffA: "Grade Cut-Off % (A)",
        GradeCutOffB: "Grade Cut-Off % (B)",
        GradeCutOffC: "Grade Cut-Off % (C)",
        GradeCutOffD: "Grade Cut-Off % (D)",
        GradeCutOffE: "Grade Cut-Off % (E)",
        GradeCutOffF: "Grade Cut-Off % (F)",
        IsGrade: "Is Grade?",
        GLAccount: "GL Account",
      };
      const changedFields = isCreate
        ? []
        : Object.keys(riskFieldLabels).filter(
            (field) =>
              this.originalRiskSnapshot &&
              payload[field] !== this.originalRiskSnapshot[field],
          );

      if (isCreate || changedFields.length > 0) {
        const title = isCreate
          ? "Confirm new Sample Code"
          : "Confirm change to an existing Sample Code";
        const message = isCreate
          ? `Sample Code <b>${payload.Code}</b> will be available to every grading/payment calculation from now on, including a recalculation of a <b>historical</b> period that never had this code available — this table has no season/date scoping to prevent that. This cannot be undone once saved.<br><br>Are you sure you want to continue?`
          : `Changing <b>${changedFields
              .map((f) => riskFieldLabels[f])
              .join(", ")}</b> for Sample Code <b>${
              payload.Code
            }</b> means any grading/payment calculation already performed using the old value can no longer be accurately recreated — this table has no season/date scoping to preserve it. This cannot be undone once saved.<br><br>Are you sure you want to continue?`;

        const confirmed = await this.confirmRiskChange(message, title);
        if (!confirmed) {
          this.$notify("Change not saved.", "info");
          return null;
        }
        payload.Confirmed = true;
      }

      return payload;
    },
    /**
     * BR-06/BR-09: Rank and Grade Cut-Off % (A-F) default to 0/0.00. The
     * split-view template resets every formModel field to undefined on Add
     * New before this hook runs (same reason Moisture Threshold's own
     * MoistureThresholdPcnt needs this), so the default is applied here
     * rather than relying on the initial data() value.
     */
    recordPostProcess(data) {
      // BR-12/AD-05: snapshot the risk-relevant fields exactly as loaded,
      // before any of this method's own create-mode defaults below are
      // applied - only meaningful for an existing record (edit/review);
      // stays undefined on Create, where every save is unconditionally
      // risk-relevant regardless of any snapshot.
      if (this.baseView.mode === "edit" || this.baseView.mode === "review") {
        this.originalRiskSnapshot = {
          GradeCutOffA: data.GradeCutOffA,
          GradeCutOffB: data.GradeCutOffB,
          GradeCutOffC: data.GradeCutOffC,
          GradeCutOffD: data.GradeCutOffD,
          GradeCutOffE: data.GradeCutOffE,
          GradeCutOffF: data.GradeCutOffF,
          IsGrade: data.IsGrade,
          GLAccount: data.GLAccount,
        };
      }
      if (data.Rank === undefined || data.Rank === null) {
        data.Rank = 0;
      }
      if (data.GLAccount === undefined || data.GLAccount === null) {
        data.GLAccount = 0;
      }
      if (
        data.IsVisibleOnGradingDetails === undefined ||
        data.IsVisibleOnGradingDetails === null
      ) {
        // BR-11/DF-06: checked by default on new records.
        data.IsVisibleOnGradingDetails = "Y";
      }
      ["A", "B", "C", "D", "E", "F"].forEach((letter) => {
        const field = "GradeCutOff" + letter;
        if (data[field] === undefined || data[field] === null) {
          data[field] = 0.0;
        }
      });
      return data;
    },
  },
  created() {
    this.beforeInit();
  },
};
</script>
